import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, Drawer, DrawerBody, DrawerContent, DrawerOverlay, Flex, IconButton, Spinner, Text, useBreakpointValue, useToast,
} from "@chakra-ui/react";
import { FiBell, FiBellOff, FiChevronDown, FiLock, FiMessageSquare, FiMinus, FiX } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { tt } from "../../i18n/tt";
import { useUser } from "../../contexts/UserContext";
import { isImpersonating } from "../../lib/policyConsentRules";
import {
  FALLBACK_POLL_MS, OPS_CHAT_OPEN_EVENT, STATE_POLL_MS, VIEWING_PING_MS, errorCodeOf, fetchGuideOn, fetchMyTopic, fetchMyState,
  fetchRoomMuted, fetchThread, markRead, sendMessage, setRoomMuted, setViewing, subscribeOpsChat,
} from "../../lib/opsChat";
import useSupportInquiryVisible from "../../hooks/useSupportInquiryVisible";
import { OPS_CHAT_BUTTON, OPS_CHAT_BUTTON_SIZE, announceOpsChatOpen, onLayOpenChange } from "../../lib/floatingStack";
import OpsChatMessages from "./OpsChatMessages";
import OpsChatComposer from "./OpsChatComposer";
import SupportGuide from "../support/SupportGuide";

/**
 * 크리에이터 화면 오른쪽 아래 「운영팀 대화」 (C안 · 2026-10-23 · docs/ops/ops-chat-room-plan.md §3-1)
 * 2026-10-28 메신저 P1(docs/ops/ops-chat-messenger-plan.md §1-1 · §1-2 · §5-1):
 *   · 열린 방이 없을 때도 버튼이 보인다(문의 길잡이 스위치 ops_chat.guide 켜짐 · 문의하기가 열린 때) — 누르면 문의 길잡이
 *   · 크리에이터가 방을 바로 만드는 버튼은 없다 — 길잡이를 거쳐 서버가 판정한 상담사 연결로만(ops_chat_connect)
 *   · 상담 상태: 연결 대기 → 상담 중 → 상담 끝(입력칸 잠김 · 「레이에게 물어보기」)
 *   · 새 메시지는 Realtime 신호로 바로(끊기면 30초 다시 읽기) · 방을 연 동안 「보고 있는 중」(그 방 푸시 건너뜀) · 방 알림 끄기
 *   · 자리: 레이 버튼 바로 위(src/lib/floatingStack.js). 레이 창이 열려 있으면 숨고, 이 창이 열려 있으면 레이 버튼이 숨는다.
 *   · 회원으로 보기(impersonate) 중에는 보내기·읽음·연결 잠금.
 */
const DISMISS_KEY = "laeebly_ops_chat_bubble_dismissed";
const readDismissed = () => { try { return localStorage.getItem(DISMISS_KEY); } catch { return null; } };
const writeDismissed = (v) => { try { localStorage.setItem(DISMISS_KEY, v); } catch { /* 저장 못 해도 말풍선만 다시 뜬다 */ } };

const creatorErrorText = (code) => ({
  too_big: tt("a.8c5e62a0", "파일 하나는 10MB까지 올릴 수 있어요."),
  bad_type: tt("a.76dbbbfa", "사진·PDF·엑셀·CSV·워드·한글 파일만 올릴 수 있어요."),
  empty_file: tt("a.3585e736", "빈 파일은 올릴 수 없어요."),
  too_many_attachments: tt("a.90fc6a77", "파일은 한 번에 5개까지 보낼 수 있어요."),
  room_closed: tt("a.d53187cb", "상담이 끝나서 더는 보낼 수 없어요. 새로 물어보려면 레이에게 먼저 물어봐 주세요."),
  rate_limited: tt("a.5753015b", "너무 빨리 보내고 있어요. 잠시 뒤 다시 보내 주세요."),
  too_long: tt("a.587fad48", "메시지는 2,000자까지 보낼 수 있어요."),
}[code] || tt("a.e81937e0", "보내지 못했어요. 잠시 뒤 다시 시도해 주세요."));

function sessionChip(room) {
  if (room.status === "closed") return { label: tt("a.76a20e13", "상담 끝"), bg: "neutral.100", color: "neutral.600" };
  if (room.session_status === "waiting") return { label: tt("a.f7c5b6d7", "연결 대기"), bg: "statusWash.warning", color: "status.warning" };
  return { label: tt("a.a828c81d", "상담 중"), bg: "brand.50", color: "brand.700" };
}

export default function OpsChatLauncher() {
  const { userInfo } = useUser();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const rollout = useSupportInquiryVisible(userInfo?.role);
  const [state, setState] = useState(null);
  const [guideOn, setGuideOn] = useState(false);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("chat"); // chat | guide
  const [roomId, setRoomId] = useState(null); // 열린 창이 보는 방(끝나도 창을 닫기 전까지 유지)
  const [thread, setThread] = useState(null);
  const [muted, setMuted] = useState(false);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [layCovering, setLayCovering] = useState(false);
  const bottomRef = useRef(null);
  const lastCount = useRef(0);
  const impersonating = isImpersonating();
  const email = userInfo?.email;
  const role = userInfo?.role || "";
  const isPartnerOnly = role.includes("Partner") && !role.includes("Creator");

  useEffect(() => onLayOpenChange(setLayCovering), []);
  useEffect(() => { announceOpsChatOpen(open && Boolean(isMobile)); }, [open, isMobile]);
  useEffect(() => { if (layCovering && !isMobile) setOpen(false); }, [layCovering, isMobile]);
  useEffect(() => () => announceOpsChatOpen(false), []);

  const [loaded, setLoaded] = useState(false);
  const loadState = useCallback(async () => {
    if (!email) return;
    try { setState(await fetchMyState()); setLoaded(true); } catch { /* 못 읽으면 지난 값 유지 — 버튼이 깜빡이지 않게 */ }
  }, [email]);

  useEffect(() => {
    if (!email) return;
    fetchGuideOn().then(setGuideOn).catch(() => setGuideOn(false));
  }, [email]);

  // 내 채널(새 방이 열리거나 상태가 바뀌면 신호) + 1분 다시 읽기(신호가 끊겨도 늦게라도 보이게)
  useEffect(() => {
    if (!email) return undefined;
    loadState();
    let off = () => {};
    let alive = true;
    fetchMyTopic().then((topic) => { if (alive) off = subscribeOpsChat(topic, () => loadState()); }).catch(() => {});
    const iv = setInterval(() => { if (document.visibilityState === "visible") loadState(); }, STATE_POLL_MS);
    const onFocus = () => loadState();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; off(); clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [email, loadState]);

  const openChat = useCallback((id) => {
    if (!id) return;
    setRoomId(id);
    setMode("chat");
    setOpen(true);
  }, []);
  const openGuide = useCallback(() => { setMode("guide"); setOpen(true); }, []);

  // 다른 화면(문의하기)에서 「연결됐어요 → 대화방 열기」
  useEffect(() => {
    const onOpen = (e) => { if (e?.detail?.roomId) { openChat(e.detail.roomId); loadState(); } };
    window.addEventListener(OPS_CHAT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPS_CHAT_OPEN_EVENT, onOpen);
  }, [openChat, loadState]);

  // 벨·푸시 알림 링크 /home?ops_chat=open(&room=…)
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("ops_chat") !== "open" || !loaded) return;
    const target = state?.room_id || sp.get("room");
    if (target) openChat(target);
    sp.delete("ops_chat");
    sp.delete("room");
    navigate({ pathname: location.pathname, search: sp.toString() ? `?${sp}` : "" }, { replace: true });
  }, [location.search, location.pathname, navigate, state?.room_id, loaded, openChat]);

  const loadThread = useCallback(async () => {
    if (!roomId) return;
    try {
      const t = await fetchThread(roomId);
      setThread(t);
      const n = (t?.messages || []).length;
      if (n !== lastCount.current) {
        lastCount.current = n;
        if (!impersonating) markRead(roomId).then(loadState).catch(() => {});
        setTimeout(() => bottomRef.current?.scrollIntoView?.({ block: "end" }), 0);
      }
    } catch { /* 다음 신호·주기에 다시 */ }
  }, [roomId, impersonating, loadState]);

  // 방을 연 동안: 방 채널 신호 · 30초 다시 읽기 · 보고 있는 중(그 방 푸시 건너뜀)
  useEffect(() => {
    if (!open || mode !== "chat" || !roomId) return undefined;
    lastCount.current = 0;
    loadThread();
    fetchRoomMuted(roomId).then(setMuted).catch(() => {});
    const off = subscribeOpsChat(`ops_chat:room:${roomId}`, () => loadThread());
    const poll = setInterval(() => { if (document.visibilityState === "visible") loadThread(); }, FALLBACK_POLL_MS);
    const ping = () => { if (!impersonating && document.visibilityState === "visible") setViewing(roomId, true).catch(() => {}); };
    ping();
    const pingIv = setInterval(ping, VIEWING_PING_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") { ping(); loadThread(); } else if (!impersonating) setViewing(roomId, false).catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      off(); clearInterval(poll); clearInterval(pingIv);
      document.removeEventListener("visibilitychange", onVis);
      if (!impersonating) setViewing(roomId, false).catch(() => {});
    };
  }, [open, mode, roomId, loadThread, impersonating]);

  const closeChat = () => {
    setOpen(false);
    setThread(null);
    loadState();
  };

  const send = async (body, files) => {
    try {
      await sendMessage(roomId, body, files);
      await loadThread();
    } catch (err) {
      toast({ title: creatorErrorText(errorCodeOf(err)), status: "error" });
      if (errorCodeOf(err) === "room_closed") loadThread();
      throw err;
    }
  };

  const toggleMuted = async () => {
    try { await setRoomMuted(roomId, !muted); setMuted(!muted); } catch { toast({ title: tt("a.56172782", "알림 설정을 바꾸지 못했어요."), status: "error" }); }
  };

  if (!email || isPartnerOnly) return null;
  const hasRoom = Boolean(state?.room_id);
  const guideAvailable = guideOn && rollout.visible;
  if (!hasRoom && !open && !guideAvailable) return null;

  const unread = Number(state?.unread) || 0;
  const showBubble = hasRoom && !open && unread > 0 && state.latest_id && state.latest_id !== dismissed && !layCovering;
  const room = thread?.room;
  const closedRoom = room?.status === "closed";
  const waiting = room?.status === "open" && room?.session_status === "waiting";
  const adminOpened = room?.origin === "admin";
  const chip = room ? sessionChip(room) : null;

  const chatPanel = (
    <Flex direction="column" h="100%" bg="white">
      <Flex align="center" gap={2.5} px={4} py={3} borderBottom="1px solid" borderColor="neutral.100">
        <Flex w="34px" h="34px" borderRadius="full" bg={closedRoom ? "neutral.500" : "brand.600"} color="white" align="center" justify="center" fontWeight="700" flexShrink={0}>L</Flex>
        <Box flex="1" minW={0}>
          <Flex align="center" gap={1.5}>
            <Text fontSize="15px" fontWeight="700">{tt("a.53ecb0e1", "레이블리 운영팀")}</Text>
            {chip && <Box as="span" fontSize="11px" fontWeight="700" px={1.5} borderRadius="5px" bg={chip.bg} color={chip.color}>{chip.label}</Box>}
          </Flex>
          <Text fontSize="12px" color="neutral.600" noOfLines={1}>
            {waiting ? tt("a.1680e8f3", "상담사가 곧 답해 드려요") : (room?.assignee_name || "")}
            {room?.topic_label ? ` · ${room.topic_label}` : ""}
          </Text>
        </Box>
        {room && !closedRoom && (
          <IconButton variant="ghost" size="sm" aria-label={muted ? tt("a.3d9f9502", "이 방 알림 켜기") : tt("a.50640bd1", "이 방 알림 끄기")}
            icon={muted ? <FiBellOff /> : <FiBell />} onClick={toggleMuted} isDisabled={impersonating} />
        )}
        <IconButton variant="ghost" aria-label={closedRoom ? tt("a.218e2ae6", "닫기") : tt("a.e2e7fed3", "대화창 내리기")}
          icon={closedRoom ? <FiX /> : isMobile ? <FiChevronDown /> : <FiMinus />} onClick={closeChat} />
      </Flex>
      {!closedRoom && adminOpened && (
        <Flex gap={2} align="flex-start" px={4} py={2} bg="brand.50" color="brand.800" fontSize="12.5px" lineHeight="1.5">
          <Box as={FiLock} mt="3px" flexShrink={0} aria-hidden="true" />
          <Text>{tt("a.b5656bcd", "운영팀이 연 대화방이에요. 바로 답할 수 있어요.")}</Text>
        </Flex>
      )}
      <Box flex="1" overflowY="auto" px={4} py={3} bg="neutral.50">
        {thread ? (
          <OpsChatMessages messages={thread.messages} viewer="creator" compact otherReadAt={thread.other_read_at} laySummary={room?.lay_summary} />
        ) : <Flex justify="center" py={10}><Spinner color="brand.500" /></Flex>}
        <div ref={bottomRef} />
      </Box>
      {closedRoom ? (
        <Box px={4} py={4} borderTop="1px solid" borderColor="neutral.100">
          <Text fontSize="14px" fontWeight="700">{tt("a.e67860e3", "상담이 끝났어요")}</Text>
          <Text fontSize="13px" color="neutral.600" mt={1}>
            {guideAvailable
              ? tt("a.315c5da4", "지난 대화는 설정 › 운영팀 대화 기록에서 다시 볼 수 있어요. 새로 물어보려면 레이에게 먼저 물어봐 주세요.")
              : tt("a.98a40012", "지난 대화는 설정 › 운영팀 대화 기록에서 다시 볼 수 있어요. 새로 궁금한 점이 있으면 문의를 남겨 주세요.")}
          </Text>
          {guideAvailable ? (
            <Button mt={3} w="100%" colorScheme="brand" onClick={() => { setThread(null); openGuide(); }}>{tt("a.4f9b3bb6", "레이에게 물어보기")}</Button>
          ) : (
            <Button mt={3} w="100%" variant="outline" colorScheme="brand" onClick={() => { closeChat(); navigate("/support"); }}>{tt("a.7ced0703", "문의 남기기")}</Button>
          )}
        </Box>
      ) : (
        <Box px={3} pt={2} pb={isMobile ? 4 : 3} borderTop="1px solid" borderColor="neutral.100">
          {impersonating && <Text fontSize="12px" color="status.warning" mb={1}>{tt("a.46183029", "회원으로 보기 중에는 보낼 수 없어요.")}</Text>}
          <OpsChatComposer onSend={send} onError={(code) => toast({ title: creatorErrorText(code), status: "warning" })}
            disabled={impersonating} enterToSend={!isMobile} />
        </Box>
      )}
    </Flex>
  );

  const guidePanel = (
    <Box h="100%" overflowY="auto" bg="white" px={4} py={3}>
      <SupportGuide compact onClose={closeChat} onConnected={(id) => { loadState(); openChat(id); }} />
    </Box>
  );
  const panel = mode === "guide" ? guidePanel : chatPanel;
  const onButton = () => (hasRoom ? openChat(state.room_id) : openGuide());
  const dialogLabel = mode === "guide" ? tt("a.4f1a014e", "문의하기") : tt("a.def77460", "레이블리 운영팀 대화");

  return (
    <>
      {!open && !layCovering && (hasRoom || guideAvailable) && (
        <Box position="fixed" zIndex={1199} right={{ base: `${OPS_CHAT_BUTTON.right.base}px`, md: `${OPS_CHAT_BUTTON.right.md}px` }}
          bottom={{ base: `${OPS_CHAT_BUTTON.bottom.base}px`, md: `${OPS_CHAT_BUTTON.bottom.md}px` }}>
          {showBubble && (
            <Box position="absolute" right={0} bottom={`${OPS_CHAT_BUTTON_SIZE + 10}px`} w="290px" maxW="calc(100vw - 32px)" bg="white"
              borderRadius="12px" boxShadow="0 8px 24px rgba(0,0,0,0.18)" p={3.5} role="status">
              <Flex gap={2.5} align="flex-start">
                <Flex w="32px" h="32px" borderRadius="full" bg="brand.600" color="white" align="center" justify="center" fontWeight="700" flexShrink={0}>L</Flex>
                <Box flex="1" minW={0}>
                  <Text fontSize="14.5px" fontWeight="700">
                    {state.first_visit ? tt("a.70945389", "운영팀이 대화를 요청했어요") : tt("a.95ec86eb", "운영팀이 메시지를 보냈어요")}
                  </Text>
                  {state.latest_sender_name && <Text fontSize="12.5px" color="neutral.600">{state.latest_sender_name}</Text>}
                </Box>
                <IconButton size="sm" variant="ghost" mt="-6px" mr="-6px" aria-label={tt("a.48871bf1", "알림 접기")} icon={<FiX />}
                  onClick={() => { writeDismissed(state.latest_id); setDismissed(state.latest_id); }} />
              </Flex>
              {/* 줄 자르기는 안쪽 글자에 — 여백이 있는 상자에 걸면 셋째 줄이 반쯤 보인다 */}
              <Box mt={2.5} px={3} py={2} bg="neutral.50" borderRadius="8px">
                <Text fontSize="13.5px" lineHeight="1.5" color="neutral.700" noOfLines={2}>
                  {state.latest_body || (state.latest_has_attachment ? tt("a.fe9ad447", "사진·파일을 보냈어요") : "")}
                </Text>
              </Box>
              <Button mt={2.5} w="100%" colorScheme="brand" onClick={() => openChat(state.room_id)}>{tt("a.b0cd7145", "대화 확인하기")}</Button>
            </Box>
          )}
          <IconButton
            aria-label={hasRoom
              ? (unread > 0 ? tt("a.c50bbe28", "운영팀 대화 열기, 새 메시지 {{v0}}개", { v0: unread }) : tt("a.95a3d412", "운영팀 대화 열기"))
              : tt("a.55d1bc4a", "문의하기 열기")}
            icon={<FiMessageSquare size={22} />}
            w={`${OPS_CHAT_BUTTON_SIZE}px`} h={`${OPS_CHAT_BUTTON_SIZE}px`} minW={`${OPS_CHAT_BUTTON_SIZE}px`}
            borderRadius="full" colorScheme="brand" variant={hasRoom ? "solid" : "outline"} bg={hasRoom ? undefined : "white"}
            border="3px solid white" boxShadow="0 4px 12px rgba(0,0,0,0.22)"
            onClick={onButton}
          />
          {unread > 0 && (
            <Flex position="absolute" top="-4px" right="-4px" minW="20px" h="20px" px={1} borderRadius="full" bg="status.error" color="white"
              fontSize="11px" fontWeight="700" align="center" justify="center" border="2px solid white" pointerEvents="none" aria-hidden="true">
              {unread > 99 ? "99+" : unread}
            </Flex>
          )}
        </Box>
      )}

      {open && isMobile && (
        <Drawer isOpen placement="bottom" onClose={closeChat}>
          <DrawerOverlay />
          <DrawerContent h="88dvh" borderTopRadius="18px" overflow="hidden" aria-label={dialogLabel}>
            <DrawerBody p={0}>{panel}</DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
      {open && !isMobile && (
        <Box position="fixed" zIndex={1200} right={`${OPS_CHAT_BUTTON.right.md}px`} bottom={`${OPS_CHAT_BUTTON.bottom.md}px`}
          w="380px" h="min(600px, calc(100vh - 160px))" borderRadius="12px" overflow="hidden" border="1px solid" borderColor="neutral.200"
          boxShadow="0 12px 36px rgba(0,0,0,0.18)" role="dialog" aria-label={dialogLabel}>
          {panel}
        </Box>
      )}
    </>
  );
}
